import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import ViewTypes from 'modules/betting-center/view-types.js';
import { createSlice, isAnyOf } from '@reduxjs/toolkit';

const betslipActions = MojitoServices.Betslip.actions;
const IntentTypes = MojitoPresentation.Base.Intent.Types;
const reduxInstance = MojitoCore.Services.redux;
const intentActions = MojitoCore.Intents.actions;

/**
 * The name of the betting center store. Will be used to register in global redux store.
 *
 * @constant
 * @type {string}
 * @memberof Mojito.Modules.BettingCenter
 */
export const STORE_KEY = 'modules/bettingCenter';

/**
 * Defines the state of the betting center store.
 *
 * @typedef BettingCenterState
 *
 * @property {Mojito.Modules.BettingCenter.viewTypes} selectedTab - Current selected tab.
 *
 * @memberof Mojito.Modules.BettingCenter
 */
export const initialState = {
    selectedTab: ViewTypes.BETSLIP,
};

export const { reducer, actions } = createSlice({
    name: 'bettingCenter',
    initialState,
    reducers: {
        selectTab(state, { payload }) {
            state.selectedTab = payload;
        },
        reset() {
            return { ...initialState };
        },
    },
});

/**
 * Actions related to BettingCenter.
 *
 * @class BettingCenterActions
 * @name actions
 * @memberof Mojito.Modules.BettingCenter
 */

/**
 * Update selected tab.
 *
 * @function selectTab
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {Mojito.Modules.BettingCenter.viewTypes} payload - Data needed to update selected tab.
 *
 * @memberof Mojito.Modules.BettingCenter.actions
 */

/**
 * Reset BettingCenter state.
 *
 * @function reset
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @memberof Mojito.Modules.BettingCenter.actions
 */
reduxInstance.actionListener.startListening({
    actionCreator: intentActions.publishIntent,
    effect: (action, listenerApi) => {
        const { dispatch } = listenerApi;

        switch (action.payload.type) {
            case IntentTypes.SHOW_OPEN_BETS:
                dispatch(actions.selectTab(ViewTypes.OPEN_BETS));
                break;

            case IntentTypes.SHOW_BETSLIP:
                dispatch(actions.selectTab(ViewTypes.BETSLIP));
                break;
        }
    },
});

reduxInstance.actionListener.startListening({
    matcher: isAnyOf(betslipActions.addPartSuccess, betslipActions.addCompositeSuccess),
    effect: (action, listenerApi) => {
        const { dispatch } = listenerApi;
        dispatch(actions.selectTab(ViewTypes.BETSLIP));
    },
});

reduxInstance.injectReducer(STORE_KEY, reducer);
